import {
  Box,
  Button,
  Center,
  Circle,
  Container,
  Flex,
  Heading,
  HStack,
  Link,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@cardboard-ui/react';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from '@lingui/macro';
import { ItemOpenedTracker } from 'apps/TenantApp/components/ItemOpenedTracker';
import { canSaveContent } from 'components/Editor/utils';
import { ItemMenu } from 'components/Item/ItemMenu';
import debounce from 'lodash/debounce';
import React, { FC, PropsWithChildren, useCallback, useState } from 'react';
import {
  generateClientID,
  graphql,
  RelaySafeProvider,
  useFragment,
  useMutation,
  useSafeRelay,
} from 'utils/graphClient';
import { useFeatureFlag } from 'utils/sessionProvider';
import { ApplicationCrashedIcon } from '../../../../components/icons';
import { NotFoundContent } from '../NotFound';
import {
  ItemViewer_node$data,
  ItemViewer_node$key,
} from './__generated__/ItemViewer_node.graphql';
import { ItemViewer_Query } from './__generated__/ItemViewer_Query.graphql';
import { ItemViewer_renameFolderNode_Mutation } from './__generated__/ItemViewer_renameFolderNode_Mutation.graphql';
import { ItemViewer_updatePageMutation } from './__generated__/ItemViewer_updatePageMutation.graphql';
import { BridgeViewer } from './BridgeViewer';
import { EditableTitle } from './components/EditableTitle';
import { FileRenderer } from './FileRenderer';
import { FormViewer } from './FormViewer';
import { ItemSidebar } from './ItemSidebar';
import {
  FolderItemModal,
  FolderItemModalBody,
  FolderItemModalHeader,
} from './ItemViewerModal';
import { useItemViewerNavigation } from './ItemViewerNavigationProvider';
import { ItemViewerProvider, useFolderItem } from './ItemViewerProvider';
import { PageEditor } from './PageEditor';
import { PendingFilePlaceholder } from './PendingFilePlaceholder';
import { useOpenItemViewer } from './useOpenItemViewer';

const updateFolderNodeMutation = graphql`
  mutation ItemViewer_renameFolderNode_Mutation(
    $folderNodeId: ID!
    $name: String!
  ) {
    updateFolderNode(input: { folderNodeId: $folderNodeId, name: $name }) {
      folderNodeUpdated {
        ... on FolderNodeInterface {
          name
        }
        ... on Node {
          id
        }
      }
    }
  }
`;

const updatePageMutation = graphql`
  mutation ItemViewer_updatePageMutation($pageId: ID!, $data: String) {
    updatePage(input: { pageId: $pageId, data: $data }) {
      pageUpdated {
        id
        data
        todos(first: 500) {
          edges {
            node {
              id
              __typename
              ...TodoDueDate_todo
              ...TodoAssignee_todo
              text
              documentNode {
                id
                ...TodoCard_page
              }
              assignee {
                id
              }
              state
              dueDate
              publishState
              isListed
            }
          }
        }
      }
    }
  }
`;

export const ItemViewer = () => {
  return (
    <ItemViewerProvider>
      <ItemViewerShell />
    </ItemViewerProvider>
  );
};

export default ItemViewer;

const ItemViewerShell = () => {
  const { closeHandler } = useFolderItem();
  const { isOpen, itemId } = useOpenItemViewer();

  if (!itemId) {
    return null;
  }

  return (
    <FolderItemModal isOpen={isOpen} onClose={closeHandler}>
      <RelaySafeProvider
        ErrorComponent={ErrorPlaceholder}
        LoadingComponent={LoadingPlaceholder}
      >
        {itemId ? <InnerItemViewer itemId={itemId} /> : <NotFoundPlaceholder />}
      </RelaySafeProvider>
    </FolderItemModal>
  );
};

const itemQuery = graphql`
  query ItemViewer_Query($id: ID!) {
    node(id: $id) {
      id
      ...ItemViewer_node
    }
  }
`;

const nodeFragment = graphql`
  fragment ItemViewer_node on Node {
    id
    __typename
    ...FileRenderer_node
    ...PageEditor_node
    ...ItemOpenedTracker_item
    ...FormViewer_node
    ...ItemMenu_node
    ...BridgeViewer_node
    ... on AnnouncementAttachment {
      content {
        __typename
        ... on File {
          id
          persistenceState
        }
      }
    }
    ... on MemberDetailsAttachment {
      content {
        __typename
        ... on File {
          id
          name
          fileName
          mimeType
          url
          size
          persistenceState
        }
      }
    }
    ... on FolderItem {
      name
      content {
        __typename
        ... on Page {
          id
          data
        }
        ... on File {
          id
          persistenceState
        }
        ... on Form {
          id
        }
        ... on InternalBridge {
          id
          url
        }
        ... on ExternalBridge {
          id
          url
        }
      }
      canRename {
        value
      }
      canUpdate {
        value
      }
      parent: ancestors(depth: 1) {
        edges {
          node {
            ... on Folder {
              id
            }
          }
        }
      }
    }
    ... on VaultItem {
      name
      content {
        __typename
        ... on File {
          id
          persistenceState
        }
        ... on PendingFile {
          __typename
        }
      }
    }
    ... on VaultDraftItem {
      name
      draftContent: content {
        __typename
        ... on File {
          id
          persistenceState
        }
      }
    }
  }
`;

interface InnerItemViewerProps {
  itemId: string;
}

export const InnerItemViewer = ({ itemId }: InnerItemViewerProps) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const { node: nodeQuery } = useLazyLoadQuery<ItemViewer_Query>(itemQuery, {
    id: itemId,
  });

  const node = useFragment<ItemViewer_node$key>(nodeFragment, nodeQuery);

  const [updatePage] =
    useMutation<ItemViewer_updatePageMutation>(updatePageMutation);

  const nodeContent = node?.content || node?.draftContent;
  const canUpdate = !!node?.canUpdate?.value;
  const [content, setContent] = useState<string>(
    nodeContent?.__typename === 'Page' ? JSON.stringify(nodeContent.data) : '',
  );

  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const debounceSavePage = useCallback(
    debounce((id: string, newContent: string) => {
      if (!canSaveContent(newContent)) {
        return;
      }

      setIsUpdating(true);
      setHasChanged(false);
      updatePage({
        variables: {
          pageId: id,
          data: newContent,
        },
        onCompleted: () => {
          setIsUpdating(false);
        },
      });
    }, 1000),
    [],
  );

  const handleChange = useCallback(
    (newContent: string) => {
      if (canUpdate && content !== newContent) {
        setHasChanged(true);
        setContent(newContent);

        if (node?.content?.__typename === 'Page' && node.content.id) {
          debounceSavePage(node.content.id, newContent);
        }
      }
    },
    [content],
  );

  if (nodeContent) {
    switch (nodeContent.__typename) {
      case 'File': {
        switch (nodeContent.persistenceState) {
          case 'PENDING':
          case 'UPLOADING':
            return (
              <ItemViewerFrame node={node} content={content}>
                <PendingFilePlaceholder nodeId={itemId} />
              </ItemViewerFrame>
            );
          default:
            // We default to rendering the file,
            //   if the persistenceState is not known at runtime,
            //   we still (try to) render the file
            return (
              <ItemViewerFrame node={node} content={content}>
                <FileRenderer node={node} />
              </ItemViewerFrame>
            );
        }
      }
      case 'PendingFile':
        return (
          <ItemViewerFrame node={node} content={content}>
            <PendingFilePlaceholder nodeId={itemId} />
          </ItemViewerFrame>
        );
      case 'Page':
        return (
          <ItemViewerFrame
            node={node}
            content={content}
            isUpdatingPage={isUpdating}
          >
            <Flex flex={1} overflow="auto" p={3} pr={6}>
              <PageEditor
                node={node}
                onChange={handleChange}
                isUpdatingPage={isUpdating}
                changed={hasChanged}
              />
            </Flex>
          </ItemViewerFrame>
        );
      case 'Form':
        return (
          <ItemViewerFrame node={node} content={content}>
            <Flex overflow="auto" flex={1}>
              <FormViewer node={node} />
            </Flex>
          </ItemViewerFrame>
        );
      case 'ExternalBridge':
      case 'InternalBridge':
        return (
          <ItemViewerFrame node={node} content={content}>
            <BridgeViewer node={node} />
          </ItemViewerFrame>
        );
      default:
        throw `File viewer can not render content of type ${nodeContent.__typename}`;
    }
  } else {
    return <NotFoundPlaceholder />;
  }
};

const ItemViewerFrame: React.FC<
  PropsWithChildren<{
    node: ItemViewer_node$data;
    content?: string;
    isUpdatingPage?: boolean;
  }>
> = ({ children, node, isUpdatingPage }) => {
  const parent = node.parent?.edges?.[0]?.node || null;
  const parentConnectionId = parent?.id
    ? generateClientID(parent.id, 'children')
    : '';

  const { close: closeViewer } = useOpenItemViewer();
  const toast = useToast();

  const [renameFolderItem, isLoading] =
    useMutation<ItemViewer_renameFolderNode_Mutation>(updateFolderNodeMutation);

  const onQuickRename = (newName: string) => {
    if (!node?.id) {
      return;
    }
    if (newName.length <= 255) {
      renameFolderItem({
        variables: { folderNodeId: node.id, name: newName },
      });
    } else {
      toast({
        title: t`File name is too long`,
        description: t`File names cannot be longer than 255 characters. Please try again with a shorter file name.`,
        status: 'error',
      });
    }
  };

  const nodeContent = node.content || node.draftContent;

  return (
    <>
      <ItemOpenedTracker key={node.id} item={node} />
      <FolderItemModalHeader
        title={
          <HStack>
            <ItemViewerNavigation nodeId={node.id} />
            <EditableTitle
              title={node?.name || ''}
              isDisabled={!node.canRename}
              onSubmit={onQuickRename}
              isLoading={isLoading}
              autoFocus={
                nodeContent?.__typename === 'Form' && node.name === 'Unnamed'
              }
            />
          </HStack>
        }
        options={
          <ItemMenu
            node={node}
            mode="item-viewer-bar"
            connectionId={parentConnectionId}
            isUpdatingPage={isUpdatingPage}
            onCompletedDelete={closeViewer}
          />
        }
      />
      <FolderItemModalBody>
        <HStack
          spacing={0}
          flex={1}
          alignItems="stretch"
          pos="relative"
          maxW="full"
        >
          <Flex
            bg="white"
            _dark={{ bg: 'gray.700' }}
            flex={1}
            justify="center"
            w="calc(100% - var(--chakra-sizes-96))"
          >
            {children}
          </Flex>

          <ItemSidebar nodeId={node.id} />
        </HStack>
      </FolderItemModalBody>
    </>
  );
};

export const NotFoundPlaceholder: FC = () => (
  <>
    <FolderItemModalHeader title={t`Not Found`} />
    <FolderItemModalBody>
      <Center flex={1}>
        <NotFoundContent />
      </Center>
    </FolderItemModalBody>
  </>
);

export const LoadingPlaceholder = () => (
  <>
    <FolderItemModalHeader title={t`Loading...`} />
    <FolderItemModalBody>
      <Center flex={1}>
        <Spinner />
      </Center>
    </FolderItemModalBody>
  </>
);

export const ErrorPlaceholder = () => (
  <>
    <FolderItemModalHeader title={t`Error`} />
    <FolderItemModalBody>
      <Center flex={1}>
        <Container>
          <VStack spacing={5}>
            <Circle bg="red.300" size="90px">
              <ApplicationCrashedIcon size="3x" color="white" />
            </Circle>

            <VStack>
              <Heading fontSize="xl">{t`Sorry, something went wrong`}</Heading>
              <Text textAlign="center">
                {t`The item could not be loaded. Please try again later.`}
              </Text>
            </VStack>
          </VStack>
        </Container>
      </Center>
    </FolderItemModalBody>
  </>
);

const ItemViewerNavigation = ({ nodeId }: { nodeId: string }) => {
  const enabled = useFeatureFlag('FEATURE_ITEM_VIEWER_NAVIGATION');

  const { fetchNextId, fetchPreviousId } = useItemViewerNavigation();
  const nextId = fetchNextId(nodeId);
  const prevId = fetchPreviousId(nodeId);

  if (!enabled || (!nextId && !prevId)) {
    return null;
  }

  return (
    <Box marginStart={-3}>
      {prevId && (
        <ItemViewerNavigationLink toItemViewer={prevId} direction="previous" />
      )}
      {nextId && (
        <ItemViewerNavigationLink toItemViewer={nextId} direction="next" />
      )}
    </Box>
  );
};

type ItemViewerNavigationLinkProps = {
  direction: 'next' | 'previous';
  toItemViewer: string;
};

const ItemViewerNavigationLink = ({
  direction,
  ...props
}: ItemViewerNavigationLinkProps) => (
  <Button
    as={Link}
    aria-label={
      direction === 'next' ? t`Go to next item` : t`Go to previous item`
    }
    fontSize="lg"
    px={0}
    size="sm"
    variant="ghost"
    color="blackAlpha.600"
    _dark={{
      color: 'whiteAlpha.600',
    }}
    {...props}
  >
    {direction === 'next' ? (
      <FontAwesomeIcon icon={solid('circle-chevron-right')} />
    ) : (
      <FontAwesomeIcon icon={solid('circle-chevron-left')} />
    )}
  </Button>
);
